import React, { cloneElement, useEffect, useRef, useState } from 'react'
import * as Dialog from "@radix-ui/react-dialog";
import { useTranslation } from 'react-i18next';
import { X } from "lucide-react";
import { cn } from '@/lib/utils';
import { TextButton } from '@/components/common/text-button';
import { Image } from '@/components/common/image';
import { FileBgIcon } from '@/components/agent-dashboard-order-management/assets';
import { TruncatedText } from '@/components/common/truncated-text';
import { ItemDetailData } from '@/services/knowledge-get-folder-items';
import { KnowledgeEmptyIcon } from '../agent-dashboard-user-detail/assets';
import { useToast } from "@/hooks/use-toast";
import { MarkdownPreviewModal } from '../common/markdown-preview-modal';

// 骨架屏组件
const KnowledgeModalSkeleton: React.FC = () => {
  const skeletonItems = Array.from({ length: 15 });
  return (
    <div className='grid grid-cols-5 gap-2'>
      {skeletonItems.map((_, index) => (
        <div key={index} className="w-[160px] h-[160px] relative">
          <div
            className='size-full overflow-hidden flex-shrink-0'
          >
            <div className="size-full rounded-[12px] bg-gray-200 animate-pulse"></div>
          </div>
        </div>
      ))}
    </div>
  );
};


interface KnowledgeModalDetailProps {
  trigger?: React.ReactElement;
  open?: boolean;
  loading?: boolean;
  name?: string;
  knowledgeFolderItems?: ItemDetailData[];
  onOpenChange?: (open: boolean) => void;
  onSave?: (ids: string[]) => void;
}

/** 知识库详情弹窗 */
export const KnowledgeModalDetail: React.FC<KnowledgeModalDetailProps> = ({
  trigger,
  loading,
  name,
  knowledgeFolderItems,
  open: externalOpen,
  onOpenChange,
  onSave,
}) => {
  const { t } = useTranslation();
  const { toast } = useToast();

  const [internalOpen, setInternalOpen] = useState(false);
  const titleRef = useRef<HTMLHeadingElement>(null);

  const [openMarkdownPreview, setOpenMarkdownPreview] = useState(false);
  const [previewId, setPreviewId] = useState(null);

  // 判断是否使用 trigger 模式（trigger 优先）
  const isTriggerMode = !!trigger;

  // 根据模式决定使用哪个 open 状态
  const currentOpen = isTriggerMode ? internalOpen : externalOpen ?? false;

  // 处理 open 状态变化
  const handleOpenChange = (open: boolean) => {
    if (isTriggerMode) {
      setInternalOpen(open);
    } else {
      onOpenChange?.(open);
    }
  };

  // 关闭
  const handleClose = () => {
    handleOpenChange(false);
  };

  // trigger 点击处理
  const handleTriggerClick = () => {
    handleOpenChange(true);
  };

  // 弹窗打开时，强制聚焦到标题
  useEffect(() => {
    if (currentOpen) {
      // 等待DOM更新后执行聚焦（确保元素已挂载）
      setTimeout(() => {
        titleRef.current?.focus();
      }, 0);
    }
  }, [currentOpen]);

  const dialogContent = (
    <Dialog.Portal>
      <Dialog.Overlay className={cn("fixed inset-0 bg-black/50 z-50")} />
      <Dialog.Content
        className={cn(
          "flex flex-col",
          "fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2",
          "bg-white rounded-[1.25rem] shadow-lg",
          "w-[912px] min-h-[738px] max-h-[738px] flex-shrink-0",
          "z-50 overflow-hidden"
        )}>
        {/* 标题区域 */}
        <div className="flex max-h-[92px] justify-between items-center px-6 pt-6 pb-4  border-b border-[rgba(233,234,235,1)">
          <Dialog.Title
            ref={titleRef}
            tabIndex={-1}
            className='focus:outline-none text-[rgba(24,29,39,1)] text-lg font-semibold flex items-center'
          >
            <span>{name}</span>
          </Dialog.Title>
          <Dialog.Close asChild>
            <button
              className="w-10 h-10 flex justify-center items-center hover:bg-gray-100 border border-[rgba(208,213,221,1)] rounded-sm shadow-[0px_1px_2px_rgba(16,24,40,0.05)]"
              tabIndex={-1}
              onClick={handleClose}
            >
              <X className="w-4 h-4 text-[rgba(71,84,103,1)]" />
            </button>
          </Dialog.Close>
        </div>
        {/* 内容 */}
        <div className='h-full flex-1 px-6 select-none overflow-y-auto py-5'>
          {loading ? (
            <KnowledgeModalSkeleton />
          ) : knowledgeFolderItems && knowledgeFolderItems.length > 0 ? (
            <div className='grid grid-cols-5 gap-4'>
              {knowledgeFolderItems?.map((item, index) => {
                const type = item.itemUrl ? (item.itemUrl.split('.').pop() || '') : 'md'

                return (
                  <div
                    key={index}
                    className="w-[160px] h-full relative flex flex-col items-center justify-center pt-3 cursor-pointer"
                    onClick={() => {
                      if (type === 'md') {
                        setPreviewId(item.id || '');
                        setOpenMarkdownPreview(true);
                      } else {
                        window.open(item.itemUrl, '_black')
                      }
                    }}
                  >
                    <div className='relative mb-[26px]'>
                      <FileBgIcon className='w-[68px] h-[84px]' />
                      <div className={cn(
                        'absolute top-10 -left-0.5 px-2 py-0.5 rounded-[6px] text-[15px] font-semibold text-white bg-[rgba(55,93,251,1)] tracking-wide',
                        type === 'pdf' && 'bg-[rgba(223,28,65,1)]',
                      )}>
                        {type.toLocaleUpperCase()}
                      </div>
                    </div>
                    <span className='text-[rgba(16,24,40,1)] text-base font-medium line-clamp-2 break-all'>
                      {item.contentTitle}
                    </span>
                  </div>
                )
              })}
            </div>
          ) : (
            <div className='w-full h-full min-h-[500px] relative flex flex-col justify-center items-center'>
              <KnowledgeEmptyIcon className='text-[rgba(102,112,133,1)]' />
              <span className='text-base text-[rgba(102,112,133,1)]'>{t("agent_dashboard.user_detail.knowledge.detail_modal.empty_tips")}</span>
            </div>
          )}
        </div>
        {/* 操作按钮区域 */}
        <div className='w-full flex justify-end px-6 py-6 border-t border-[rgba(233,234,235,1)]'>
          <TextButton
            label={t('agent_dashboard.user_detail.material.detail_modal.close')}
            className='w-[147px] font-medium h-10 bg-white border border-[rgba(213,215,218,1)] hover:bg-gray-50 text-base !text-[rgba(65,70,81,1)]'
            onClick={handleClose}
          />
        </div>
        {openMarkdownPreview && (
          <MarkdownPreviewModal
            open={openMarkdownPreview}
            onOpenChange={setOpenMarkdownPreview}
            title={knowledgeFolderItems.find(item => item.id === previewId)?.contentTitle || ''}
            content={knowledgeFolderItems.find(item => item.id === previewId)?.itemContent || ''}
          />
        )}
      </Dialog.Content>
    </Dialog.Portal>
  )

  // trigger 模式
  if (isTriggerMode) {
    return (
      <Dialog.Root open={currentOpen} onOpenChange={handleOpenChange}>
        <Dialog.Trigger asChild>
          {cloneElement(trigger, {
            onClick: (e: React.MouseEvent) => {
              e.stopPropagation();
              e.preventDefault();
              handleTriggerClick();
              // 保留原有的 onClick 事件
              if (trigger.props.onClick) {
                trigger.props.onClick(e);
              }
            },
          })}
        </Dialog.Trigger>
        {dialogContent}
      </Dialog.Root>
    );
  }

  // open 参数模式
  return (
    <Dialog.Root open={currentOpen} onOpenChange={handleOpenChange}>
      {dialogContent}
    </Dialog.Root>
  );
}
